import { useEffect, useState } from 'react';
import { useGame } from '../context/GameContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import PageWrapper from '../components/wrapper/FlexiblePageWrapper';
import GhostWrapper from '../components/wrapper/GhostWrapper';
import GameGhosts from './../components/ghosts/GameGhosts';
import GameWrapper from '../components/wrapper/GameWrapper';
import Header from './../components/general/Header';
import Title from './../components/text/Title';
import Subtitle from './../components/text/Subtitle';
import ButtonWrapper from '../components/wrapper/ButtonWrapper';
import Button from './../components/general/Button';
import GameCard from './../components/game/GameCard';
import GameFeedbackText from '../components/game/GameFeedbackText';
import GamePlaceholder from './GamePlaceholder';
import TextLink from '../components/text/TextLink';
import GameLivesScore from '../components/game/GameLivesScore';
import Footer from './../components/general/Footer';

function Game() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { gameState, guessAutocomplete, getCurrentRound } = useGame();
  const [feedback, setFeedback] = useState(null);
  const [isMatching, setIsMatching] = useState(false);

  useEffect(() => {
    if (!gameState.gameStarted) {
      navigate('/');
    }
  }, [gameState.gameStarted, navigate]);

  const handleGuess = async (guessedText) => {
    if (guessedText.trim()) {
      setIsMatching(true);
      setFeedback(null);
      try {
        const result = await guessAutocomplete(guessedText.trim());
        setFeedback(result);
      } finally {
        setIsMatching(false);
      }
    }
  };

  const handleIntermission = () => {
    navigate('/game/intermission');
  };

  // prettier-ignore
  const handleGiveUp = () => {

    navigate(`/game/end?r1=${encodeURIComponent(gameState?.rounds[0].roundScore ?? 0)}&r2=${encodeURIComponent(gameState?.rounds[1].roundScore ?? 0)}&r3=${encodeURIComponent(gameState?.rounds[2].roundScore ?? 0)}&ts=${encodeURIComponent(gameState?.totalScore ?? 0)}`);
  };

  /* Game page while loading */
  if (gameState.isLoading) {
    return <GamePlaceholder status="loading" />;
  }

  /* Game page if error exists */
  if (gameState.error) {
    return <GamePlaceholder status="error" errorName={gameState.error} />;
  }

  /* Game page if unable to obtain currentRound*/
  const currentRound = getCurrentRound();

  if (!currentRound) {
    return <GamePlaceholder status="noCurrentRound" />;
  }

  return (
    <PageWrapper>
      <GhostWrapper>
        <Header text={t('headerTitle')} />
        <GameWrapper>
          <Title size="small" text={t('title')} />
          {/* Round number */}
          <Subtitle text={`${t('round')} ${gameState.currentRoundIndex + 1}/3`} />
          {/* Lives and total score */}
          <GameLivesScore lives={gameState.lives} totalScore={gameState.totalScore} />
          {/* Feedback message */}
          <GameFeedbackText
            isMatching={isMatching}
            isCorrect={feedback && feedback.status === 1}
            hasFeedback={feedback ? true : false}
            hasRoundFinished={feedback && feedback.roundFinished}
          />

          <GameCard
            hasRoundFinished={feedback && feedback.roundFinished ? true : false}
            text={currentRound.prompt.search}
            suggestions={currentRound.suggestions}
            correctSuggestions={currentRound.correctSuggestions}
            onGuess={handleGuess}
          />

          <ButtonWrapper>
            <TextLink text={t('giveUp')} onClick={handleGiveUp} />
            {/* Round intermission button */}
            {feedback && feedback.roundFinished && (
              <Button text={t('goToIntermission')} onClick={handleIntermission} />
            )}
          </ButtonWrapper>
        </GameWrapper>
      </GhostWrapper>
      <GameGhosts />
      <Footer />
    </PageWrapper>
  );
}

export default Game;
